import React, { useState } from 'react';
import PropTypes from 'prop-types';

import PortalDropdown, { DropdownMenuItem } from 'views_shared/app/javascript/components/PortalDropdown';

import PitchMuteModal from './PitchMuteModal';
import PitchReportModal from './PitchReportModal';
import vapi from '../../../javascript/frontend/api/vapi';
import vahoy from '../../../javascript/vahoy';
import stripHtmlToVisibleText from '../../../javascript/stripHtmlToVisibleText';

// The pitch inbox's replacement for ConversationDropdown (design feedback): inside the
// request page only user-level actions make sense — same two items as the pitch card's
// menu, wired to the same V1 mute/report flows.
function PitchUserActionsDropdown({ pitch, muteReport = null, small = false }) {
  // Multi-party threads: name the target so it is unambiguous who gets muted/reported
  const pitcherFirstName = (pitch.profile_name || '').split(' ')[0] || 'user';
  const [showMute, setShowMute] = useState(false);

  // One data mode, two id spaces. The request page (PitchInboxSection) and the messaging
  // inbox (MyInbox) both LIFT mute/report state and pass it down as muteReport, so every
  // mounted menu for the same pitcher stays in sync. Current muted/reported state comes
  // from the pitch object when the caller precomputes it (the request page's V1-serialized
  // pitches) and otherwise from the lifted id lists (the inbox's V2 API rows). The V1 ids
  // differ by row shape too: inbox rows are V2 rows where the V1 pitch id is migrate_id
  // and the pitcher's V1 user id rides user_migrate_id — both null for V2-native pitchers,
  // who have no V1 records to act on.
  const v1PitchId = 'migrate_id' in pitch ? pitch.migrate_id : pitch.id;
  const v1UserId = pitch.user_migrate_id;
  const actionable = !!(muteReport && v1PitchId && v1UserId);

  const muted = pitch.muted !== undefined
    ? !!pitch.muted
    : !!(v1UserId && (muteReport?.mutedUserIds || []).includes(v1UserId));
  const reported = pitch.reported !== undefined
    ? !!pitch.reported
    : !!(v1PitchId && (muteReport?.reportedPitchIds || []).includes(v1PitchId));

  const [muteDuration, setMuteDuration] = useState(`${muteReport?.defaultMuteDurationDays || 30}`);
  const [showReportAbuse, setShowReportAbuse] = useState(false);
  const [reportDescription, setReportDescription] = useState('');
  const [reportBehaviorTypes, setReportBehaviorTypes] = useState();

  const recordMutedChange = (nowMuted, mute = null) => muteReport.onMutedChange?.(v1UserId, nowMuted, mute);
  const recordReported = () => muteReport.onReported?.(v1PitchId);

  const submitMute = () => {
    vapi.muteUser({
      duration_days: muteDuration,
      muted_user_id: v1UserId,
      reporting_user_id: muteReport.reportingUserId,
    }).then(({ data }) => {
      setShowMute(false);
      recordMutedChange(true, data);
      globalThis.Qwoted.showToast?.({
        toastTitle: `${pitcherFirstName} muted`,
        toastBody: 'New pitches from them will be hidden. They can still reply within existing conversations.',
        variant: 'success',
      });
    }).catch((error) => {
      globalThis.Qwoted.showToast?.({
        toastTitle: 'Error',
        toastBody: 'Failed to mute the user. Please try again.',
        variant: 'danger',
      });
      throw error;
    });
    vahoy.track('MuteModal#muteUser');
  };

  const submitUnmute = () => {
    vapi.unmuteUser({
      muted_user_id: v1UserId,
      reporting_user_id: muteReport.reportingUserId,
    }).then(() => {
      recordMutedChange(false);
      globalThis.Qwoted.showToast?.({
        toastTitle: `${pitcherFirstName} unmuted`,
        toastBody: "You'll receive their new pitches again.",
        variant: 'success',
      });
    }).catch((error) => {
      globalThis.Qwoted.showToast?.({
        toastTitle: 'Error',
        toastBody: 'Failed to unmute the user. Please try again.',
        variant: 'danger',
      });
      throw error;
    });
    vahoy.track('PitchUserActionsDropdown#unmuteUser');
  };

  const toggleBehaviorType = (key) => {
    setReportBehaviorTypes((prev) => {
      const next = [...(prev || [])];
      const index = next.indexOf(key);
      if (index >= 0) {
        next.splice(index, 1);
      } else {
        next.push(key);
      }
      return next;
    });
  };

  const submitReportAbuse = () => {
    vapi.reportAbuse({
      report_description: reportDescription,
      reported_behavior_types: reportBehaviorTypes || [],
      reported_id: v1PitchId,
      reported_text: stripHtmlToVisibleText(pitch.pitch_text || ''),
      reported_type: 'Pitch',
      reported_url: window.location.href,
      reported_user_id: v1UserId,
      reporting_user_id: muteReport.reportingUserId,
    }).then(() => {
      setShowReportAbuse(false);
      recordReported();
      globalThis.Qwoted.showToast?.({
        toastTitle: 'Report submitted',
        toastBody: 'Thanks for flagging this — our team will review it.',
        variant: 'success',
      });
    }).catch((error) => {
      globalThis.Qwoted.showToast?.({
        toastTitle: 'Error',
        toastBody: 'Failed to submit the report. Please try again.',
        variant: 'danger',
      });
      throw error;
    });
    vahoy.track('ReportModal#reportAbuse');
  };

  return (
    <>
      {/* Toggle styling matches ConversationDropdown so pitch and conversation menus
          read as one system (same hover state, same icon size, same small row variant) */}
      <PortalDropdown
        toggleAriaLabel="Pitch actions"
        toggleClassName="btn btn-secondary btn-icon rounded-2 text-neutral-80 fs-5 border-0 shadow-none"
        toggleStyle={small ? { width: 28, height: 28, '--bs-btn-bg': 'transparent' } : undefined}
      >
        {muted ? (
          <DropdownMenuItem
            icon="fa-solid fa-volume-high"
            label={`Unmute ${pitcherFirstName}`}
            disabled={!actionable}
            onClick={submitUnmute}
          />
        ) : (
          <DropdownMenuItem
            icon="fa-solid fa-volume-xmark"
            label={`Mute ${pitcherFirstName}`}
            disabled={!actionable}
            onClick={() => setShowMute(true)}
          />
        )}
        <DropdownMenuItem
          icon={`fa-${reported ? 'solid' : 'regular'} fa-flag`}
          label={reported ? 'Reported' : 'Report pitch'}
          disabled={!actionable || reported}
          className="text-destructive-80"
          onClick={() => setShowReportAbuse(true)}
        />
      </PortalDropdown>

      {actionable && (
        <PitchMuteModal
          show={showMute}
          onHide={() => setShowMute(false)}
          durationDays={muteDuration}
          onDurationChange={(e) => setMuteDuration(e.target.value)}
          defaultMuteDurationDays={muteReport.defaultMuteDurationDays}
          onSubmit={submitMute}
        />
      )}
      {actionable && (
        <PitchReportModal
          show={showReportAbuse}
          onHide={() => setShowReportAbuse(false)}
          abusiveBehaviorTypes={muteReport.abusiveBehaviorTypes}
          behaviorTypes={reportBehaviorTypes}
          onToggleBehaviorType={toggleBehaviorType}
          onDescriptionChange={(e) => setReportDescription(e.target.value)}
          onSubmit={submitReportAbuse}
        />
      )}
    </>
  );
}

PitchUserActionsDropdown.propTypes = {
  pitch: PropTypes.shape({
    id: PropTypes.number.isRequired,
    migrate_id: PropTypes.number,
    user_migrate_id: PropTypes.number,
    pitch_text: PropTypes.string,
    profile_name: PropTypes.string,
    muted: PropTypes.bool,
    reported: PropTypes.bool,
  }).isRequired,
  muteReport: PropTypes.shape({
    reportingUserId: PropTypes.number,
    defaultMuteDurationDays: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types -- server-serialized behavior-types map
    abusiveBehaviorTypes: PropTypes.object,
  }),
  small: PropTypes.bool,
};

export default PitchUserActionsDropdown;
